import React, { useEffect, useState } from "react";
import SubjectListing from "./SubjectListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddSubjectDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/SubjectSlice";
import AddSubjectWrapper from "../Add/AddSubjectWrapper";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import EditSubjectWrapper from "../Edit/EditSubjectWrapper";
import {
  useChangeStatusSubjectByIdMutation,
  useDeleteSubjectByIdMutation,
  useGetAllSubjectDataQuery,
} from "src/services/SubjectService";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const SubjectListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.subject);

  const [deleteSubject] = useDeleteSubjectByIdMutation();
  const [chnageStatusSubject] = useChangeStatusSubjectByIdMutation();

  const [selectedSubjectId, setSelectedSubjectId] = useState<string>();
  const { data, isLoading, isFetching } = useGetAllSubjectDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["subjectName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteSubject = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Subject ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteSubject(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusSubject = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Subject ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          chnageStatusSubject({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const canEdit = usePermission("SUBJECT", "UPDATE");
  const canDelete = usePermission("SUBJECT", "DELETE");
  const canActive = usePermission("SUBJECT", "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    { field: "subjectName", headerName: "Subject Name", flex: "flex-[1_1_0%]" },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      align: "end",
      renderCell: (row: any) => {
        const menuOptions = [];

        if (canEdit) {
          menuOptions.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedSubjectId(row?._id);
            },
          });
        }

        if (canActive) {
          menuOptions.push({
            label: "Active/Deactive",
            onClick: () => handleChangeStatusSubject(row?._id),
          });
        }

        if (canDelete) {
          menuOptions.push({
            label: "Delete",
            onClick: () => handleDeleteSubject(row?._id),
          });
        }

        return <ATMMenu options={menuOptions} />;
      },
    },
  ];

  return (
    <>
      <div>
        <SubjectListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setIsOpenAddDialog: (value) =>
              dispatch(setIsOpenAddSubjectDialog(value)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddSubjectWrapper
          onClose={() => dispatch(setIsOpenAddSubjectDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditSubjectWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedSubjectId={selectedSubjectId || ""}
        />
      ) : null}
    </>
  );
};

export default SubjectListingWrapper;
